import React from 'react';
import ColorPicker from '../LoaderGenerator/ColorPicker';
import { findNodeById } from './viewTreeUtils';
import CommonModifiers from './CommonModifiers';

const ViewPropertiesPanel = ({ viewTree, selectedViewId, onUpdateProperties }) => {
  if (!selectedViewId) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-500 text-sm">
        <p>Select a view from the tree to edit its properties</p>
      </div>
    );
  }

  const findResult = findNodeById(selectedViewId, viewTree);
  if (!findResult) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-500 text-sm">
        <p>View not found</p>
      </div>
    );
  }

  const { node } = findResult;
  const { type, properties = {} } = node;

  const handlePropertyChange = (key, value) => {
    onUpdateProperties(selectedViewId, { [key]: value });
  };

  const handleModifiersChange = (modifiers) => {
    onUpdateProperties(selectedViewId, { modifiers });
  };

  const renderProperties = () => {
    switch (type) {
      case 'HStack':
      case 'VStack':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Spacing: {properties.spacing || 8}px
              </label>
              <input
                type="range"
                min="0"
                max="40"
                value={properties.spacing || 8}
                onChange={(e) => handlePropertyChange('spacing', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Alignment
              </label>
              <div className="grid grid-cols-2 gap-2">
                {type === 'HStack' ? (
                  <>
                    {['leading', 'center', 'trailing'].map((align) => (
                      <button
                        key={align}
                        onClick={() => handlePropertyChange('alignment', align)}
                        className={`
                          px-3 py-2 rounded text-xs font-medium transition-all
                          ${
                            properties.alignment === align
                              ? 'bg-gradient-primary text-gray-900 dark:text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                          }
                        `}
                      >
                        {align.charAt(0).toUpperCase() + align.slice(1)}
                      </button>
                    ))}
                  </>
                ) : (
                  <>
                    {['top', 'center', 'bottom'].map((align) => (
                      <button
                        key={align}
                        onClick={() => handlePropertyChange('alignment', align)}
                        className={`
                          px-3 py-2 rounded text-xs font-medium transition-all
                          ${
                            properties.alignment === align
                              ? 'bg-gradient-primary text-gray-900 dark:text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                          }
                        `}
                      >
                        {align.charAt(0).toUpperCase() + align.slice(1)}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        );

      case 'ZStack':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alignment
            </label>
            <div className="grid grid-cols-3 gap-2">
              {['topLeading', 'top', 'topTrailing', 'leading', 'center', 'trailing', 'bottomLeading', 'bottom', 'bottomTrailing'].map((align) => (
                <button
                  key={align}
                  onClick={() => handlePropertyChange('alignment', align)}
                  className={`
                    px-2 py-1 rounded text-xs font-medium transition-all
                    ${
                      properties.alignment === align
                        ? 'bg-primary-600 text-gray-900 dark:text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }
                  `}
                >
                  {align.replace(/([A-Z])/g, ' $1').trim()}
                </button>
              ))}
            </div>
          </div>
        );

      case 'Text':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Content
              </label>
              <textarea
                value={properties.content || ''}
                onChange={(e) => handlePropertyChange('content', e.target.value)}
                className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
                rows="3"
                placeholder="Enter text content"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Font Size: {properties.fontSize || 16}pt
              </label>
              <input
                type="range"
                min="10"
                max="72"
                value={properties.fontSize || 16}
                onChange={(e) => handlePropertyChange('fontSize', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Font Weight
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['regular', 'medium', 'semibold', 'bold'].map((weight) => (
                  <button
                    key={weight}
                    onClick={() => handlePropertyChange('fontWeight', weight)}
                    className={`
                      px-3 py-2 rounded text-xs font-medium transition-all
                      ${
                        properties.fontWeight === weight
                          ? 'bg-primary-600 text-gray-900 dark:text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {weight.charAt(0).toUpperCase() + weight.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Text Color
              </label>
              <ColorPicker
                color={properties.color || '#000000'}
                onChange={(color) => handlePropertyChange('color', color)}
                label="Color"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Alignment
              </label>
              <div className="grid grid-cols-3 gap-2">
                {['leading', 'center', 'trailing'].map((align) => (
                  <button
                    key={align}
                    onClick={() => handlePropertyChange('alignment', align)}
                    className={`
                      px-3 py-2 rounded text-xs font-medium transition-all
                      ${
                        properties.alignment === align
                          ? 'bg-primary-600 text-gray-900 dark:text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {align.charAt(0).toUpperCase() + align.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'Button':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title
              </label>
              <input
                type="text"
                value={properties.title || ''}
                onChange={(e) => handlePropertyChange('title', e.target.value)}
                className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
                placeholder="Button Title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Style
              </label>
              <div className="grid grid-cols-1 gap-2">
                {['plain', 'bordered', 'borderedProminent'].map((style) => (
                  <button
                    key={style}
                    onClick={() => handlePropertyChange('style', style)}
                    className={`
                      px-3 py-2 rounded text-xs font-medium transition-all
                      ${
                        properties.style === style
                          ? 'bg-primary-600 text-gray-900 dark:text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {style.replace(/([A-Z])/g, ' $1').trim()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'Image':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Source (systemName or asset)
              </label>
              <input
                type="text"
                value={properties.source || ''}
                onChange={(e) => handlePropertyChange('source', e.target.value)}
                className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
                placeholder="star.fill"
              />
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Use SF Symbols name (e.g., "star.fill") or asset name
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Size: {properties.size || 24}pt
              </label>
              <input
                type="range"
                min="16"
                max="100"
                value={properties.size || 24}
                onChange={(e) => handlePropertyChange('size', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Aspect Ratio
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['fit', 'fill'].map((ratio) => (
                  <button
                    key={ratio}
                    onClick={() => handlePropertyChange('aspectRatio', ratio)}
                    className={`
                      px-3 py-2 rounded text-xs font-medium transition-all
                      ${
                        properties.aspectRatio === ratio
                          ? 'bg-primary-600 text-gray-900 dark:text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {ratio.charAt(0).toUpperCase() + ratio.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={properties.resizable !== false}
                  onChange={(e) => handlePropertyChange('resizable', e.target.checked)}
                  className="rounded"
                />
                Resizable
              </label>
            </div>
          </div>
        );

      case 'Spacer':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Min Length: {properties.minLength || 0}px
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={properties.minLength || 0}
              onChange={(e) => handlePropertyChange('minLength', parseInt(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Set to 0 for automatic spacing
            </p>
          </div>
        );

      case 'Divider':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Color
              </label>
              <ColorPicker
                color={properties.color || '#CCCCCC'}
                onChange={(color) => handlePropertyChange('color', color)}
                label="Color"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Thickness: {properties.thickness || 1}px
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={properties.thickness || 1}
                onChange={(e) => handlePropertyChange('thickness', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        );

      case 'List':
      case 'ScrollView':
        return (
          <div className="text-center py-4 text-gray-600 dark:text-gray-400 text-sm">
            <p>Container views don't have properties. Add child views to build content.</p>
          </div>
        );

      default:
        return (
          <div className="text-center py-4 text-gray-600 dark:text-gray-400 text-sm">
            <p>No properties available for this view type.</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200 dark:border-gray-700 pb-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {type} Properties
        </h3>
      </div>
      {renderProperties()}
      
      {/* Common Modifiers */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
        <CommonModifiers
          modifiers={properties.modifiers || {}}
          onUpdate={handleModifiersChange}
        />
      </div>
    </div>
  );
};

export default ViewPropertiesPanel;
